import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Inject, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject, firstValueFrom } from 'rxjs';
import { convertToEmailSafeHtml } from 'src/app/_helpers/email-html.helper';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { SendEmailRequest } from 'src/app/models/email.model';
import { EmailService } from 'src/app/services/email.service';
import { ToastService } from 'src/app/services/toast.service';
import { Lookup } from 'src/app/models/lookup.model';
import { CustomEntity } from 'src/app/models/customEntity.model';
import { CustomFieldTypeEnum } from 'src/app/enums/customFieldType.enum';
import { EndpointsTableComponent } from 'src/app/components/endpoints-table/endpoints-table.component';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';
import { EmailCompositionService, EmailFormState } from 'src/app/components/report-generation-popup/services/email-form.service';

export interface SendEmailDialogData {
  preSelectedAttachment?: FileAttachment;
  preSelectedAttachments?: FileAttachment[];
  preSelectedRecipientId?: string;
  defaultSubject?: string;
  defaultBody?: string;
  entity?: any;
  organizationLevels?: Lookup[];
}

@Component({
  standalone: false,
  selector: 'app-send-email-dialog',
  templateUrl: './send-email-dialog.component.html',
  styleUrls: ['./send-email-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [EmailCompositionService]
})
export class SendEmailDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private recipientEntityMap = new Map<string, string>();

  isLoading = false;
  isSending = false;
  emailForm: EmailFormState;
  employeeFullNameById = new Map<string, string>();
  editingChip: { email: string; type: 'recipient' | 'cc' } | null = null;

  initialBoundEmployee: CustomEntity | null = null;
  selectedEmployeeId: string | null = null;
  selectedCcEmployeeId: string | null = null;
  employeeSearchTerm = '';
  ccEmployeeSearchTerm = '';
  selectedAttachments: FileAttachment[] = [];

  constructor(
    private dialogRef: MatDialogRef<SendEmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SendEmailDialogData,
    private emailService: EmailService,
    private emailCompositionService: EmailCompositionService,
    private toastService: ToastService,
    private dialog: MatDialog
  ) {
    this.emailForm = this.emailCompositionService.createEmptyForm();
  }

  ngOnInit(): void {
    this.initializeAttachments();
    this.initializeForm();
    this.setupEmployeeSearch();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupScrollListeners();
  }

  private initializeAttachments(): void {
    if (this.data?.preSelectedAttachments?.length) {
      this.selectedAttachments = [...this.data.preSelectedAttachments];
    } else if (this.data?.preSelectedAttachment) {
      this.selectedAttachments = [this.data.preSelectedAttachment];
    }
  }

  private initializeForm(): void {
    if (this.data?.defaultSubject) this.emailForm.subject = this.data.defaultSubject;
    if (this.data?.defaultBody) this.emailForm.message = this.data.defaultBody;
  }

  private setupEmployeeSearch(): void {
    this.emailCompositionService.setupSearch(
      term => this.loadEmployeesFor('recipient', term),
      term => this.loadEmployeesFor('cc', term)
    );
  }

  private async loadInitialData(): Promise<void> {
    await this.emailCompositionService.loadCustomFieldsAsync();
    if (this.emailCompositionService.customFields.length > 0) {
      await this.emailCompositionService.loadEmployeesAsync();
      if (this.data?.entity) {
        setTimeout(() => {
          this.autoPopulateRecipient();
          this.autoPopulateCcRecipients();
        }, 100);
      }
    }
  }

  private autoPopulateRecipient(): void {
    const endPoint = this.data?.entity?.endPoint;
    if (!endPoint) return;

    const emailField = this.emailCompositionService.findFieldByType(CustomFieldTypeEnum.HrisEmailId);
    if (!emailField || !endPoint[emailField.property]) return;

    const employeeEmail = endPoint[emailField.property];
    if (!this.emailCompositionService.isValidEmail(employeeEmail)) return;

    this.emailForm.recipient = employeeEmail;
    this.recipientEntityMap.set(employeeEmail, endPoint.id);
    this.selectedEmployeeId = endPoint.id;

    this.emailCompositionService.loadEmployeeByIdAsync(endPoint.id, this.data?.organizationLevels).then(employee => {
      if (employee) {
        this.initialBoundEmployee = employee;
        if (!this.emailCompositionService.filteredEmployees.some(e => e.id === endPoint.id)) {
          this.emailCompositionService.filteredEmployees = [employee, ...this.emailCompositionService.filteredEmployees];
        }
        this.rebuildEmployeeNameMap();
      }
    });
  }

  private autoPopulateCcRecipients(): void {
    const ccEmails: string[] = [];
    const endPoint = this.data?.entity?.endPoint;

    if (endPoint) {
      const supervisorEmailField = this.emailCompositionService.findFieldByType(CustomFieldTypeEnum.HrisSupervisorEmail);
      if (supervisorEmailField && endPoint[supervisorEmailField.property]) {
        const supervisorEmail = endPoint[supervisorEmailField.property];
        if (this.emailCompositionService.isValidEmail(supervisorEmail)) ccEmails.push(supervisorEmail);
      }
    }

    const hygienistEmail = this.data?.entity?.managingHygienistUserName;
    if (hygienistEmail && this.emailCompositionService.isValidEmail(hygienistEmail) && !ccEmails.includes(hygienistEmail)) {
      ccEmails.push(hygienistEmail);
    }

    this.emailForm.ccRecipients = ccEmails;
  }

  private rebuildEmployeeNameMap(): void {
    const rebuilt = new Map<string, string>();
    const allEmployees = [
      ...this.emailCompositionService.filteredEmployees,
      ...this.emailCompositionService.filteredCcEmployees
    ];
    allEmployees.forEach(emp => {
      rebuilt.set(emp.id, this.emailCompositionService.getEmployeeFullName(emp));
    });
    this.employeeFullNameById = rebuilt;
  }

  async loadEmployeesForDropdown(searchTerm = ''): Promise<void> {
    await this.emailCompositionService.loadEmployeesAsync(searchTerm);
    this.rebuildEmployeeNameMap();
  }

  async loadEmployeesFor(target: 'recipient' | 'cc', searchTerm = ''): Promise<void> {
    await this.emailCompositionService.loadEmployeesWithBoundAsync(
      searchTerm,
      target,
      target === 'recipient' ? this.initialBoundEmployee : null
    );
    this.rebuildEmployeeNameMap();
  }

  onEmployeeSelected(employeeId: string): void {
    if (!employeeId) {
      this.selectedEmployeeId = null;
      return;
    }

    const selected = this.emailCompositionService.employees.find(e => e.id === employeeId);
    if (!selected) return;

    const email = this.emailCompositionService.getEmployeeEmail(selected);

    if (email && this.emailCompositionService.isValidEmail(email)) {
      this.selectedEmployeeId = employeeId;
      this.emailForm.recipient = email;
      this.recipientEntityMap.clear();
      this.recipientEntityMap.set(email, selected.id);
    } else {
      this.toastService.displayErrorMessage(email ? 'Invalid email address' : 'Employee email not found', true);
      this.selectedEmployeeId = null;
    }
  }

  onCcEmployeeSelected(employeeId: string): void {
    if (!employeeId) {
      this.selectedCcEmployeeId = null;
      return;
    }

    const selected = this.emailCompositionService.employees.find(e => e.id === employeeId);
    if (!selected) return;

    const email = this.emailCompositionService.getEmployeeEmail(selected);

    if (email && this.emailCompositionService.isValidEmail(email)) {
      if (!this.emailForm.ccRecipients.includes(email)) {
        this.emailForm.ccRecipients.push(email);
      }
      this.selectedCcEmployeeId = null;
    }
  }

  filterEmployeesLocally(searchTerm: string): void {
    this.employeeSearchTerm = searchTerm;
    this.emailCompositionService.triggerRecipientSearch(searchTerm);
    if (!searchTerm.trim() && this.initialBoundEmployee) {
      this.selectedEmployeeId = this.initialBoundEmployee.id;
    }
  }

  filterCcEmployeesLocally(searchTerm: string): void {
    this.ccEmployeeSearchTerm = searchTerm;
    this.emailCompositionService.triggerCcSearch(searchTerm);
  }

  onEmployeeDropdownScroll(event: any): void {
    const threshold = 100;
    const position = event.target.scrollTop + event.target.offsetHeight;
    const height = event.target.scrollHeight;

    if (position > height - threshold && !this.emailCompositionService.isLoading && this.emailCompositionService.hasMore) {
      void this.emailCompositionService
        .loadMoreEmployeesAsync(this.employeeSearchTerm || this.ccEmployeeSearchTerm)
        .then(() => this.rebuildEmployeeNameMap());
    }
  }

  attachScrollListener(isOpen: boolean): void {
    if (!isOpen) return;

    if (this.emailCompositionService.employees.length === 0) this.loadEmployeesForDropdown();

    setTimeout(() => {
      const panels = document.querySelectorAll('.multiple-select-panel');
      const panel = panels.length > 0 ? panels[panels.length - 1] : null;
      if (panel) {
        panel.removeEventListener('scroll', this.handlePanelScroll);
        panel.addEventListener('scroll', this.handlePanelScroll);
      }
    }, 150);
  }

  private handlePanelScroll = (event: Event): void => this.onEmployeeDropdownScroll(event);

  private cleanupScrollListeners(): void {
    document.querySelectorAll('.multiple-select-panel').forEach(panel => {
      panel.removeEventListener('scroll', this.handlePanelScroll);
    });
  }

  clearSelectedEmployee(): void {
    this.selectedEmployeeId = null;
    this.emailForm.recipient = '';
    this.recipientEntityMap.clear();
    this.initialBoundEmployee = null;
  }

  getEmployeeFullName(employee: CustomEntity): string {
    return this.emailCompositionService.getEmployeeFullName(employee);
  }

  getSelectedEmployeeDisplay(): string {
    if (!this.selectedEmployeeId) return '';

    const employee = this.emailCompositionService.employees.find(e => e.id === this.selectedEmployeeId)
      || (this.initialBoundEmployee?.id === this.selectedEmployeeId ? this.initialBoundEmployee : null);

    if (!employee) return '';
    return this.emailCompositionService.getEmployeeEmail(employee) || this.emailCompositionService.getEmployeeFullName(employee);
  }

  getSelectedCcEmployeeDisplay(): string {
    if (!this.selectedCcEmployeeId) return '';
    const employee = this.emailCompositionService.employees.find(e => e.id === this.selectedCcEmployeeId);
    return employee ? this.emailCompositionService.getEmployeeFullName(employee) : '';
  }

  openEmployeeSearchDialog(isForCC = false): void {
    if (!this.emailCompositionService.coId) return;

    const data: ISearchFieldData = {
      coId: this.emailCompositionService.coId,
      selected: isForCC ? null : this.selectedEmployeeId,
      isMultiple: false
    };

    this.dialog.open(EndpointsTableComponent, {
      width: '960px',
      data,
      panelClass: 'resizable-dialog'
    }).afterClosed().subscribe(result => {
      if (!result?.value || !result?.items) return;

      const email = this.emailCompositionService.getEmployeeEmail(result.items);
      if (!this.emailCompositionService.isValidEmail(email)) return;

      if (isForCC) {
        if (!this.emailForm.ccRecipients.includes(email)) {
          this.emailForm.ccRecipients.push(email);
        }
      } else {
        this.emailForm.recipient = email;
        this.selectedEmployeeId = result.items.id;
        this.recipientEntityMap.clear();
        this.recipientEntityMap.set(email, result.items.id);
      }
    });
  }

  removeEmailRecipient(): void {
    this.emailForm.recipient = '';
    this.recipientEntityMap.clear();
  }

  saveEmailRecipient(newEmail: string): void {
    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) {
      this.emailForm.recipient = '';
      return;
    }

    if (!this.emailCompositionService.isValidEmail(trimmedEmail)) {
      this.toastService.displayErrorMessage('Invalid email format', true);
      return;
    }

    this.emailForm.recipient = trimmedEmail;
  }

  addCcRecipient(): void {
    this.addEmailToList(this.emailForm.ccInput, this.emailForm.ccRecipients, 'ccInput');
  }

  removeCcRecipient(email: string): void {
    this.emailForm.ccRecipients = this.emailForm.ccRecipients.filter(e => e !== email);
  }

  editCcRecipient(email: string): void {
    this.editingChip = { email, type: 'cc' };
  }

  saveCcRecipient(oldEmail: string, newEmail: string): void {
    this.updateEmailInList(oldEmail, newEmail, this.emailForm.ccRecipients);
  }

  cancelEditRecipient(): void {
    this.editingChip = null;
  }

  removeAttachment(attachment: FileAttachment): void {
    this.selectedAttachments = this.selectedAttachments.filter(a => a.id !== attachment.id);
  }

  async sendEmail(): Promise<void> {
    if (!this.validateEmailForm()) return;

    const recipientEntityId = this.recipientEntityMap.get(this.emailForm.recipient);
    if (!recipientEntityId) {
      this.toastService.displayErrorMessage('Could not find recipient employee ID', true);
      return;
    }

    if (this.selectedAttachments.length === 0) {
      this.toastService.displayErrorMessage('Please attach at least one file', true);
      return;
    }

    try {
      this.isSending = true;

      const payload: SendEmailRequest = {
        recipientEntityId,
        subject: this.emailForm.subject,
        body: convertToEmailSafeHtml(this.emailForm.message),
        ccRecipients: this.emailForm.ccRecipients.length > 0 ? this.emailForm.ccRecipients : undefined,
        fileAttachmentIds: this.selectedAttachments.map(a => a.id)
      };

      await firstValueFrom(this.emailService.sendEmail(payload));
      this.dialogRef.close({ success: true });
    } catch (error) {
      this.toastService.displayErrorMessage('Failed to send email', true);
    } finally {
      this.isSending = false;
    }
  }

  cancel(): void {
    this.dialogRef.close({ success: false });
  }

  private addEmailToList(input: string, list: string[], inputField: 'recipientInput' | 'ccInput'): void {
    const email = input.trim();
    if (!email) return;

    if (!this.emailCompositionService.isValidEmail(email)) {
      this.toastService.displayErrorMessage('Invalid email format', true);
      return;
    }

    if (list.includes(email)) {
      this.toastService.displayErrorMessage('Email already added', true);
      return;
    }

    list.push(email);
    this.emailForm[inputField] = '';
  }

  private updateEmailInList(oldEmail: string, newEmail: string, list: string[]): void {
    const trimmedEmail = newEmail.trim();

    if (!trimmedEmail || trimmedEmail === oldEmail) {
      this.editingChip = null;
      return;
    }

    if (!this.emailCompositionService.isValidEmail(trimmedEmail)) {
      this.toastService.displayErrorMessage('Invalid email format', true);
      this.editingChip = null;
      return;
    }

    if (list.includes(trimmedEmail)) {
      this.toastService.displayErrorMessage('Email already exists', true);
      this.editingChip = null;
      return;
    }

    const index = list.indexOf(oldEmail);
    if (index !== -1) list[index] = trimmedEmail;
    this.editingChip = null;
  }

  private validateEmailForm(): boolean {
    const validations = [
      { check: !this.emailForm.recipient?.trim(), msg: 'Please add a recipient email' },
      { check: !this.emailForm.subject?.trim(), msg: 'Subject is required' },
      { check: !this.emailForm.message?.trim(), msg: 'Message is required' }
    ];

    const failed = validations.find(v => v.check);
    if (failed) {
      this.toastService.displayErrorMessage(failed.msg, true);
      return false;
    }
    return true;
  }

  // ==================== Template Getters/Setters ====================

  get canSendEmail(): boolean {
    return !!this.emailForm.recipient?.trim() &&
      !!this.emailForm.subject?.trim() &&
      !!this.emailForm.message?.trim() &&
      this.selectedAttachments.length > 0;
  }

  get emailValidationMessages(): string[] {
    const messages: string[] = [];
    if (!this.emailForm.recipient?.trim()) messages.push('Recipient\'s Email is required');
    if (!this.emailForm.subject?.trim()) messages.push('Subject is required');
    if (!this.emailForm.message?.trim()) messages.push('Message is required');
    if (this.selectedAttachments.length === 0) messages.push('At least one attachment is required');
    return messages;
  }

  get emailWarningMessages(): string[] {
    return this.emailForm.ccRecipients.length === 0 ? ['No CC recipients added'] : [];
  }

  get hasSelectedEmployee(): boolean {
    return this.selectedEmployeeId != null;
  }

  // Template bindings for HTML compatibility
  get filteredEmployees(): CustomEntity[] {
    return this.emailCompositionService.filteredEmployees;
  }

  get filteredCcEmployees(): CustomEntity[] {
    return this.emailCompositionService.filteredCcEmployees;
  }

  get isLoadingMoreEmployees(): boolean {
    return this.emailCompositionService.isLoading;
  }

  get ccRecipientInput(): string {
    return this.emailForm.ccInput;
  }

  set ccRecipientInput(value: string) {
    this.emailForm.ccInput = value;
  }

  get ccRecipients(): string[] {
    return this.emailForm.ccRecipients;
  }

  get emailSubject(): string {
    return this.emailForm.subject;
  }

  set emailSubject(value: string) {
    this.emailForm.subject = value;
  }

  get emailMessage(): string {
    return this.emailForm.message;
  }

  set emailMessage(value: string) {
    this.emailForm.message = value;
  }

  get employeeTypeProperty(): string {
    return this.emailCompositionService.employeeFields.employeeType;
  }

  get emailProperty(): string {
    return this.emailCompositionService.employeeFields.email;
  }

  get positionProperty(): string {
    return this.emailCompositionService.employeeFields.position;
  }

  public trackById = trackById;
  public trackByIndex = trackByIndex;
}